import React from 'react';
import { cn } from '@/lib/utils';

interface PitechLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
  variant?: 'dots' | 'pulse' | 'orbit' | 'bars';
}

export const PitechLoader: React.FC<PitechLoaderProps> = ({ 
  size = 'md',
  className,
  text,
  variant = 'orbit'
}) => {
  const sizeMap = {
    sm: { container: 'w-6 h-6', pi: 'text-[8px]', text: 'text-xs' },
    md: { container: 'w-10 h-10', pi: 'text-xs', text: 'text-sm' },
    lg: { container: 'w-12 h-12', pi: 'text-sm', text: 'text-sm' },
    xl: { container: 'w-14 h-14', pi: 'text-base', text: 'text-base' },
  };

  const { container, pi, text: textSize } = sizeMap[size];

  if (variant === 'dots') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
        <div className="flex items-center gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={cn(pi, 'font-bold text-primary')}
              style={{
                animation: `pitech-bounce 1.4s ease-in-out ${i * 0.1}s infinite`,
              }}
            >
              π
            </span>
          ))}
        </div>
        {text && <span className={cn('text-muted-foreground font-medium', textSize)}>{text}</span>}
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-4', className)}>
        <div className={cn('relative', container)}>
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
          <div className="absolute inset-2 rounded-full bg-primary/40 animate-ping" style={{ animationDelay: '0.2s' }} />
          <div className="absolute inset-4 rounded-full bg-primary/60 animate-ping" style={{ animationDelay: '0.4s' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-primary" />
          </div>
        </div>
        {text && <span className={cn('text-muted-foreground font-medium', textSize)}>{text}</span>}
      </div>
    );
  }

  if (variant === 'bars') {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
        <div className="flex items-end gap-1 h-8">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-1.5 bg-gradient-to-t from-primary to-primary/40 rounded-full"
              style={{
                animation: `pitech-bars 1.2s ease-in-out ${i * 0.1}s infinite`,
              }}
            />
          ))}
        </div>
        {text && <span className={cn('text-muted-foreground font-medium', textSize)}>{text}</span>}
      </div>
    );
  }

  // Default: orbit variant with Pi symbols
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <div className={cn('relative', container)}>
        {/* Outer rotating ring */}
        <div 
          className="absolute inset-0 rounded-full border-2 border-transparent"
          style={{
            borderTopColor: 'hsl(var(--primary))',
            borderRightColor: 'hsl(var(--primary) / 0.3)',
            animation: 'pitech-spin 1s linear infinite',
          }}
        />
        
        {/* Center Pi symbol */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className={cn('font-bold text-primary', pi)}
            style={{
              textShadow: '0 0 8px hsl(var(--primary) / 0.5)',
              animation: 'pitech-glow 1.5s ease-in-out infinite',
            }}
          >
            π
          </span>
        </div>

        {/* Orbiting Pi symbols */}
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              animation: `pitech-orbit 2.5s linear ${i * 0.8}s infinite`,
            }}
          >
            <span 
              className={cn('absolute top-0 left-1/2 -translate-x-1/2 font-semibold text-primary/70', pi)}
              style={{
                textShadow: '0 0 4px hsl(var(--primary) / 0.4)',
              }}
            >
              π
            </span>
          </div>
        ))}
      </div>
      {text && (
        <span className={cn('text-muted-foreground font-medium', textSize)}>
          {text}
        </span>
      )}
    </div>
  );
};

// Full page loading overlay with blur backdrop
export const PitechPageLoader: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col items-center gap-4 p-6 rounded-2xl bg-card/50 border border-border/50 shadow-2xl">
        <PitechLoader size="lg" variant="orbit" />
        <span className="text-sm font-medium text-muted-foreground">{text}</span>
      </div>
    </div>
  );
};

// Inline loading for buttons and small areas
export const PitechInlineLoader: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-current"
          style={{
            animation: `pitech-bounce 1s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </div>
  );
};

// Skeleton loader for content placeholders
export const PitechSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div 
      className={cn(
        'rounded-lg bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%]',
        className
      )}
      style={{
        animation: 'pitech-shimmer 1.5s ease-in-out infinite',
      }}
    />
  );
};
